<template>
  <div class="agencies-container">
    <TableFiltersContainer padding="4px 0px 4px 0px">
      <AwTextField
        v-model="contractsState.selectedFilters.search"
        leading-icon="i-ph-magnifying-glass"
        placeholder="Rechercher par talent"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <TeamMemberSelect
        :team-member-ids="teamMemberIdsFromSelection"
        :roles="['consultant_team_member']"
        @update:team-member-ids="onLegacyTeamMemberIdsUpdate"
      />
      <BusinessUnitSelect v-model:business-unit-ids="contractsState.selectedFilters.businessUnitIds" />
      <TableStatusSelect
        v-model:status="contractsState.selectedFilters.status"
        :status-options="contractStatusOptions"
      />
      <TableFiltersResetButton @click="resetFilters" />
    </TableFiltersContainer>
    <div class="mt-2 mb-2">
      <AwDisplayData
        :items="[
          {
            label: 'Coefficient moyen',
            value: averageCoefficientData ? averageCoefficientData.toFixed(2).toString() : '-',
            helperText: 'Coefficient moyen de tous les contrats de cette société',
          },
          {
            label: 'Taux horaire moyen',
            value: averageHourlyWageData ? averageHourlyWageData.toFixed(2).toString() : '-',
          },
          {
            label: 'Taux facturé moyen',
            value:
              averageCoefficientData && averageHourlyWageData
                ? (averageCoefficientData * averageHourlyWageData).toFixed(2).toString()
                : '-',
          },
        ]"
      />
    </div>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="contractsState.sorting"
      :loading="contractsState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :data="contracts"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
      :on-select="(row: any) => navigateTo(`/contracts/${row.original.id}`, { open: { target: '_blank' } })"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <AwBadge :color="mapStatusToColor(row.original.status)">
            {{
              getStatusBadgeData(row.original.status, isCurrentUserBadgedAsValidator(row.original.status)).translation
            }}
          </AwBadge>
        </TableCellContainer>
      </template>
      <template #owner-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TableCellTeamMember :team-member="row.original.owner" />
        </TableCellContainer>
      </template>
      <template #talent-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TableCellTalent :talent="row.original.talent" />
        </TableCellContainer>
      </template>
      <template #start_date-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TableCellDate
            v-if="row.original.start_date"
            :date="row.original.start_date"
            :sub-date="row.original.end_date"
          />
          <div v-else>-</div>
        </TableCellContainer>
      </template>
      <template #agency-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{ row.original.agency?.name || "-" }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #coefficient-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{ row.original.coefficient || "-" }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #amendments-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <TableCellContractAmendment v-if="row.original.contract_key" :contract-key="row.original.contract_key" />
        </TableCellContainer>
      </template>
      <template #business_unit-cell="{ row }">
        <TableCellContainer :is-loading="contractsState.loading" :skeletons="['text']">
          <div class="flex items-center gap-2">
            <TextEllipsis>
              {{ row.original.owner?.business_unit?.emoji }} {{ row.original.owner?.business_unit?.name }}
            </TextEllipsis>
          </div>
        </TableCellContainer>
      </template>
      <template #footer>
        <TablePagination
          :total-items="contractsState.totalItems"
          :page-size="contractsState.selectedFilters.pageSize ?? 20"
          :page-index="contractsState.selectedFilters.pageIndex ?? 0"
          @update:page-size="contractsState.selectedFilters.pageSize = $event"
          @update:page-index="contractsState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "~/composables/useToast";
import type { z } from "zod";
import { formatDateForDB } from "@asap/shared/utils";
import type { TableEmptyStateType } from "~/utils/types";
import { useTableColumn } from "~/composables/useTableColumn";
import { useContract, useContracts } from "~/components/contract/composables/useContract";
import type { ContractSchema } from "@asap/shared/schemas/contract/list";
import { ContractStatusSchema, type ContractStatus } from "@asap/shared/types/supabase.types.gen";

const props = defineProps<{
  companyId: string;
}>();

const contracts = ref<ContractSchema[]>([]);
const averageHourlyWageData = ref<number | null>(null);
const averageCoefficientData = ref<number | null>(null);

const emptyStateType = ref<TableEmptyStateType>("no-data");

const { addToastError } = useToast();
const { getStatusBadgeData, isCurrentUserBadgedAsValidator } = useContract();
const { contractsState, selectMultipleForTable, convertSortingToOrders, mapStatusToColor } = useContracts();

const teamMemberIdsFromSelection = computed(() =>
  contractsState.selectedFilters.teamMemberSelection
    ?.filter((e): e is { type: "id"; value: string } => e.type === "id")
    .map((e) => e.value)
);

const onLegacyTeamMemberIdsUpdate = (ids: string[] | undefined) => {
  contractsState.selectedFilters.teamMemberSelection =
    ids === undefined || ids.length === 0 ? undefined : ids.map((id) => ({ type: "id" as const, value: id }));
};

const contractStatusOptions = computed(() => {
  return ContractStatusSchema.options.map((option: z.ZodLiteral<ContractStatus>) => {
    const status: ContractStatus = option.value;
    return {
      label: getStatusBadgeData(status, isCurrentUserBadgedAsValidator(status)).translation,
      value: status,
    };
  });
});

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: "Statut",
      value: {
        accessorKey: "status",
        header: "Statut",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Consultant",
      value: {
        accessorKey: "owner",
        header: "Consultant",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Talent",
      value: {
        accessorKey: "talent",
        header: "Talent",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Début/Fin",
      value: {
        accessorKey: "start_date",
        header: "Début/Fin",
        maxSize: 120,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Agence",
      value: {
        accessorKey: "agency",
        header: "Agence",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Coef",
      value: {
        accessorKey: "coefficient",
        header: "Coef",
        maxSize: 55,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Avenants",
      value: {
        accessorKey: "amendments",
        header: "Avenants",
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Business Unit",
      value: {
        accessorKey: "business_unit",
        header: "Business Unit",
      },
      blocked: true,
      selected: true,
    },
  ],
  "company-contracts-table-columns"
);

const resetFilters = () => {
  contractsState.selectedFilters.search = undefined;
  contractsState.selectedFilters.status = undefined;
  contractsState.selectedFilters.businessUnitIds = undefined;
  contractsState.selectedFilters.teamMemberSelection = undefined;
  contractsState.sorting = [{ id: "created_at", desc: true }];
};

const query = async function () {
  try {
    const orders = convertSortingToOrders(contractsState.sorting);
    const { data, count, contractsQueryData } = await selectMultipleForTable({
      ...contractsState.selectedFilters,
      startDate: formatDateForDB(new Date()),
      companyId: props.companyId,
      orders,
    });
    if (data) contracts.value = data;
    if (count !== null) contractsState.totalItems = count;
    if (contractsQueryData) {
      averageCoefficientData.value = contractsQueryData.averageCoefficient;
      averageHourlyWageData.value = contractsQueryData.averageHourlyWage;
    }
    if (contractsState.selectedFilters.search) {
      emptyStateType.value = "no-data-with-filters";
    } else {
      emptyStateType.value = "no-data";
    }
  } catch (error) {
    emptyStateType.value = "error";
    addToastError(
      {
        title: "Une erreur est survenue lors de la récupération des contrats",
      },
      error
    );
  }
};

const isInitialized = ref(false);

onMounted(async () => {
  contractsState.selectedFilters.pageIndex = 0;
  await query();
  isInitialized.value = true;
});

watch(() => contractsState.selectedFilters.pageIndex, query);

watch(
  () => [
    contractsState.sorting,
    contractsState.selectedFilters.search,
    contractsState.selectedFilters.pageSize,
    contractsState.selectedFilters.status,
    contractsState.selectedFilters.companyId,
    contractsState.selectedFilters.orders,
    contractsState.selectedFilters.businessUnitIds,
    contractsState.selectedFilters.teamMemberSelection,
  ],
  async () => {
    if (!isInitialized.value) return;
    contractsState.selectedFilters.pageIndex = 0;
    await query();
  }
);
</script>

<style scoped lang="scss">
.agencies-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.toggle_wrapper {
  margin: 0 auto;
  margin-top: 1rem;
}
</style>
